import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import {
    fetchReferralTableItemsByPk,
    fetchReferralTableScan
} from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash.debounce";
import ToggleSwitch from "../shared/ToggleSwitch";
import Select from 'react-select';

const searchOptions = [
    { value: 'pk', label: 'referralUuid (pk)', additional: { pkKey: 'referralUuid', skKey: 'tableVersion' } },
    { value: 'patientIdIndex', label: 'patientIdIndex', additional: { pkKey: 'patientId', skKey: 'created' } },
    { value: 'providerUserIndex', label: 'providerUserIndex', additional: { pkKey: 'providerUser', skKey: 'created' } },
    { value: 'referredByPcpTinIndex', label: 'referredByPcpTinIndex', additional: { pkKey: 'referredByPcpTin', skKey: 'created' } },
    { value: 'referredToPcpTinIndex', label: 'referredToPcpTinIndex', additional: { pkKey: 'referredToPcpTin', skKey: 'created' } },
    { value: 'systemIdentifierIndex', label: 'systemIdentifierIndex', additional: { pkKey: 'systemIdentifier', skKey: 'version' } },
    { value: 'versionSystemIdentifierIndex', label: 'versionSystemIdentifierIndex', additional: { pkKey: 'version', skKey: 'systemIdentifier' } },
]

const ReferralTable = () => {

    const [showMoreColumns, setShowMoreColumns] = useState(false);
    const [selectedSearchOption, setSelectedSearchOption] = useState(null);
    const [searchPkValue, setSearchPkValue] = useState();
    const [decouncedSearchPkValue, setDecouncedSearchPkValue] = useState();
    const [searchSkValue, setSearchSkValue] = useState();
    const [decouncedSearchSkValue, setDecouncedSearchSkValue] = useState();
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);

    const { ref, inView } = useInView();

    const queryKey = ['referral-table', selectedSearchOption?.value, decouncedSearchPkValue, decouncedSearchSkValue];

    const {
        data,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        isLoading,
        isFetching,
        hasNextPage,
        isFetched,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError,
        status,
        error
    } = useInfiniteQuery({
        retry: false,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: true,
        queryKey,
        queryFn: ({ pageParam }) => {
            let params = { lastEvaluatedKey: pageParam };

            if (selectedSearchOption?.value && selectedSearchOption.value !== 'pk') {
                params.indexName = selectedSearchOption.value
            }

            if (decouncedSearchPkValue && selectedSearchOption?.value) {
                if (decouncedSearchSkValue) {
                    params.skKey = selectedSearchOption?.additional?.skKey
                    params.skValue = decouncedSearchSkValue
                }

                return fetchReferralTableItemsByPk({
                    ...params,
                    pkKey: selectedSearchOption?.additional?.pkKey,
                    pkValue: decouncedSearchPkValue
                })
            }

            return fetchReferralTableScan(params)
        },
        select: (data) => {
            return {
                pages: data.pages.map((page) => page.Items?.map(item => item)).flatMap((x) => x),
                pageParams: data.pageParams,
            };
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.LastEvaluatedKey ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey)) : null
        },
        staleTime: 0,
        enabled: true
    })

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchPkValue(event.target.value?.trim());
        }, 500),
        []
    );

    const debouncedSkChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchSkValue(event.target.value?.trim());
        }, 500),
        []
    );

    const items = data?.pages.flat();

    let columns = ['referralUuid', 'tableVersion', 'patientId', 'systemIdentifier', 'referredByNpi', 'referredToPcpNpi', 'submittedBy', 'submittedByUser', 'systemIdentifier', 'version', 'created', 'status'];

    if (showMoreColumns) {
        columns = [
            ...columns,
            'providerUser', 'referredByPcpTin', 'referredToPcpTin'
        ];
    }

    return (
        <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                    <p className="title">Referral Table</p>
                    <p className="sub-title">pk: referralUuid -- sk: tableVersion | GSIs: patientIdIndex, providerUserIndex, referredByPcpTinIndex, referredToPcpTinIndex, systemIdentifierIndex, versionSystemIdentifierIndex</p>
                </div>
                <div>
                    <ToggleSwitch
                        label={`Auto Refresh`}
                        onToggle={(val) => setAutoRefreshEnabled(val)}
                        initialState={autoRefreshEnabled}
                    />
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                    <Select
                        name={'searchOptions'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        options={searchOptions}
                        value={selectedSearchOption}
                        onChange={(option) => {
                            setSelectedSearchOption(option);
                            setSearchPkValue('');
                            setDecouncedSearchPkValue('');
                            setSearchSkValue('');
                            setDecouncedSearchSkValue('');
                        }}
                    />

                    {selectedSearchOption?.value && <>
                        <input
                            style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                            type="text"
                            id={"search"}
                            placeholder={selectedSearchOption?.additional?.pkKey}
                            autoComplete="off"
                            onChange={(e) => {
                                setSearchPkValue(e.target.value);
                                debouncedChangeHandler(e);
                            }}
                            value={searchPkValue}
                        />
                        <input
                            style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                            type="text"
                            id={"sk"}
                            placeholder={selectedSearchOption?.additional?.skKey}
                            autoComplete="off"
                            onChange={(e) => {
                                setSearchSkValue(e.target.value);
                                debouncedSkChangeHandler(e);
                            }}
                            value={searchSkValue}
                        />
                    </>}
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setSelectedSearchOption(null)
                        setSearchPkValue('');
                        setDecouncedSearchPkValue('');
                        setSearchSkValue('');
                        setDecouncedSearchSkValue('')
                    }}
                >
                    Clear filters
                </p>
                <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>
            </div>

            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"referral-table"}
                        columns={columns}
                        items={items}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        ref={ref}
                    />
                </>
            )}
        </div>
    )
}

export default ReferralTable
